import { useEffect, useRef, useState } from 'preact/hooks';
import { MAX_RECORDING_SECONDS } from '@feedback/shared';
import { strings } from '../strings';
import type { RecordingResult, RecordingSession, ScreenRecorder } from '../recording/recorder';

/** Segments in the budget meter. Squares, never a ring (ADR 0030). */
const METER_SEGMENTS = 10;

export interface RecordingPanelProps {
  recorder: ScreenRecorder;
  /** Called with the finished clip. The caller turns it into an attachment. */
  onRecorded: (result: RecordingResult) => void;
  disabled?: boolean;
}

type Phase = 'idle' | 'confirming' | 'recording' | 'error';

/**
 * Record the screen, behind a step that says what that means.
 *
 * **The confirm step is the decision, not decoration.** Pressing Record raises
 * the browser's own screen-share picker and, if the reporter allows it, a
 * microphone prompt. A permission dialog nobody expected is how people learn to
 * dismiss permission dialogs, so the widget says what it is about to ask for
 * and what it will capture, and the reporter presses again. That is the ruling
 * recorded on I19222 and it is the reason an embedded third-party script is
 * allowed to raise those prompts at all.
 *
 * **The meter is squares and it counts the budget, not the audio.** The design
 * language bans circles and `prohibited-values.test.ts` enforces it
 * (ADR 0021), which rules out the audio-reactive ring this control usually
 * gets. That is the smaller half of the argument. The larger half is that a
 * level meter tells a reporter something they cannot act on, and how much of
 * their thirty seconds is left is something they can.
 */
export function RecordingPanel({ recorder, onRecorded, disabled }: RecordingPanelProps) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [elapsed, setElapsed] = useState(0);
  const session = useRef<RecordingSession | null>(null);

  // Abandons a recording still running when the modal closes under it. Without
  // this the screen stays shared and the browser keeps showing its sharing
  // banner over the host page, which is the widget doing something to someone
  // else's tab after it is gone.
  useEffect(() => {
    return () => session.current?.cancel();
  }, []);

  useEffect(() => {
    if (phase !== 'recording') return;
    const timer = window.setInterval(() => {
      setElapsed(session.current?.elapsedSeconds() ?? 0);
    }, 250);
    return () => window.clearInterval(timer);
  }, [phase]);

  const start = async () => {
    try {
      const started = await recorder.start();
      session.current = started;
      setElapsed(0);
      setPhase('recording');

      // Awaited here rather than only on the Stop button, because the recorder
      // also stops itself: at the duration cap, at the size cap, and when the
      // reporter uses the browser's own "Stop sharing" control. All four end up
      // on this line.
      const result = await started.stop();
      session.current = null;
      setPhase('idle');
      onRecorded(result);
    } catch {
      // A refused screen-share lands here and is an ordinary answer, not a
      // fault. It says so and leaves the rest of the form alone; nothing about
      // a declined recording should block submitting a report.
      session.current = null;
      setPhase('error');
    }
  };

  const stop = () => {
    void session.current?.stop();
  };

  if (phase === 'recording') {
    const filled = Math.min(Math.round((elapsed / MAX_RECORDING_SECONDS) * METER_SEGMENTS), METER_SEGMENTS);
    return (
      <div class="ft-recording">
        <div class="ft-recording-status" role="status">
          <span class="ft-recording-time">
            {strings.recording.elapsed(elapsed, MAX_RECORDING_SECONDS)}
          </span>
          <span class="ft-recording-meter" aria-hidden="true">
            {Array.from({ length: METER_SEGMENTS }, (_, index) => (
              <span
                key={index}
                class={index < filled ? 'ft-recording-segment ft-recording-segment-on' : 'ft-recording-segment'}
              />
            ))}
          </span>
        </div>
        <button type="button" class="ft-recording-stop" onClick={stop}>
          {strings.recording.stopLabel}
        </button>
      </div>
    );
  }

  if (phase === 'confirming') {
    return (
      <div class="ft-recording ft-recording-confirm">
        <p class="ft-recording-explainer">{strings.recording.explainer(MAX_RECORDING_SECONDS)}</p>
        <div class="ft-recording-confirm-actions">
          <button type="button" class="ft-capture-action" onClick={() => setPhase('idle')}>
            {strings.recording.cancelLabel}
          </button>
          <button type="button" class="ft-recording-start" onClick={() => void start()}>
            {strings.recording.confirmLabel}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div class="ft-recording">
      <button
        type="button"
        class="ft-screenshot-button"
        onClick={() => setPhase('confirming')}
        disabled={disabled}
      >
        {strings.recording.startLabel}
      </button>
      {phase === 'error' ? (
        <p class="ft-hint" role="alert">
          {strings.recording.errorMessage}
        </p>
      ) : null}
    </div>
  );
}
